import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Slot } from '@radix-ui/react-slot'

import ArrowLeftIcon from '@wwt/shared/assets/icons/outline/arrow-left.svg'
import ArrowRightIcon from '@wwt/shared/assets/icons/outline/arrow-right.svg'

import { cn } from '@/shared/lib/cn'

import { Button, ButtonProps } from './Button'

type NestedMenuContextProps = {
	currentPath: string[]
	open: (path: string) => void
	goBack: () => void
}

const NestedMenuContext = React.createContext<NestedMenuContextProps | null>(
	null
)

// eslint-disable-next-line react-refresh/only-export-components
export const useNestedMenu = () => {
	const context = React.use(NestedMenuContext)

	if (!context) {
		throw new Error('useNestedMenuContext must be used within a <NestedMenu />')
	}

	return context
}

export interface NestedMenuProps {
	children: React.ReactNode
}

export const NestedMenu = ({ children }: NestedMenuProps) => {
	const [currentPath, setCurrentPath] = React.useState<string[]>([])

	const goBack = React.useCallback(() => {
		setCurrentPath(currentPath.slice(0, -1))
	}, [currentPath])

	const open = React.useCallback(
		(path: string) => {
			setCurrentPath([...currentPath, path])
		},
		[currentPath]
	)

	return (
		<NestedMenuContext
			value={{
				currentPath,
				open,
				goBack
			}}
		>
			{children}
		</NestedMenuContext>
	)
}

export interface NestedMenuTitleProps {
	pathToString: (path: string | undefined) => string
}

export const NestedMenuTitle = ({ pathToString }: NestedMenuTitleProps) => {
	const { currentPath } = useNestedMenu()
	return pathToString(currentPath[currentPath.length - 1])
}

export interface NestedMenuItemProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	to?: string
	asChild?: boolean
}

export const NestedMenuItem = ({
	to,
	children,
	className,
	onClick,
	asChild = false,
	...props
}: NestedMenuItemProps) => {
	const Comp = asChild ? Slot : 'button'
	const { open } = useNestedMenu()

	const handleClick = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (to) open(to)
			onClick?.(e)
		},
		[onClick, open, to]
	)

	return (
		<Comp
			className={cn(
				'bg-background text-foreground hover:bg-muted focus-visible:ring-primary disabled:text-input flex h-12 items-center justify-between px-4 py-3 text-base font-normal transition-colors ring-inset focus-visible:ring-1 focus-visible:outline-hidden disabled:pointer-events-none disabled:cursor-not-allowed',
				className
			)}
			{...props}
			onClick={handleClick}
		>
			<span className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:size-6 [&_svg]:self-start">
				{children}
			</span>
			{to && <ArrowRightIcon />}
		</Comp>
	)
}

export interface NestedMenuContentProps {
	className?: string
	children: React.ReactNode
	path: string | 'root'
}

export const NestedMenuContent = ({
	className,
	children,
	path
}: NestedMenuContentProps) => {
	const { currentPath } = useNestedMenu()

	const isActive =
		path === 'root' ? currentPath.length === 0 : currentPath.at(-1) === path

	return (
		<div
			className={cn(
				'grid gap-x-8',
				isActive && 'grid-cols-1 *:data-nested:hidden sm:grid-cols-2',
				!isActive && '[&>:not([data-nested])]:hidden',
				className
			)}
			data-nested
		>
			{children}
		</div>
	)
}

export const NestedMenuBackButton = ({ className, ...props }: ButtonProps) => {
	const { t } = useTranslation()
	const { currentPath, goBack } = useNestedMenu()

	if (currentPath.length === 0) return null

	return (
		<Button
			className={cn('[&_svg]:size-4', className)}
			variant="flat"
			size="icon-sm"
			{...props}
			onClick={goBack}
		>
			<ArrowLeftIcon />
			<span className="sr-only">{t('goBack')}</span>
		</Button>
	)
}
